import React, { useState, useEffect } from 'react'
import { User, Calculator, Save, AlertTriangle, CheckCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useToastHelpers } from '../../contexts/ToastContext'
import {
  UserTaxProfile as UserTaxProfileType,
  TaxProfileFormState,
  TAX_BRACKETS_2024,
  TaxProfileUpdateRequest
} from '../../types/tax-analysis'

interface UserTaxProfileProps {
  profile?: UserTaxProfileType | null
  onProfileUpdate?: (profile: UserTaxProfileType) => void
  loading?: boolean
}

const US_STATES = [
  { code: 'AL', name: 'Alabama', rate: 0.05 },
  { code: 'AK', name: 'Alaska', rate: 0.00 },
  { code: 'AZ', name: 'Arizona', rate: 0.025 },
  { code: 'AR', name: 'Arkansas', rate: 0.065 },
  { code: 'CA', name: 'California', rate: 0.133 },
  { code: 'CO', name: 'Colorado', rate: 0.0463 },
  { code: 'CT', name: 'Connecticut', rate: 0.0699 },
  { code: 'DE', name: 'Delaware', rate: 0.066 },
  { code: 'FL', name: 'Florida', rate: 0.00 },
  { code: 'GA', name: 'Georgia', rate: 0.0575 },
  { code: 'HI', name: 'Hawaii', rate: 0.11 },
  { code: 'ID', name: 'Idaho', rate: 0.0605 },
  { code: 'IL', name: 'Illinois', rate: 0.0495 },
  { code: 'IN', name: 'Indiana', rate: 0.0323 },
  { code: 'IA', name: 'Iowa', rate: 0.0853 },
  { code: 'KS', name: 'Kansas', rate: 0.057 },
  { code: 'KY', name: 'Kentucky', rate: 0.05 },
  { code: 'LA', name: 'Louisiana', rate: 0.06 },
  { code: 'ME', name: 'Maine', rate: 0.0715 },
  { code: 'MD', name: 'Maryland', rate: 0.0575 },
  { code: 'MA', name: 'Massachusetts', rate: 0.09 },
  { code: 'MI', name: 'Michigan', rate: 0.0425 },
  { code: 'MN', name: 'Minnesota', rate: 0.0985 },
  { code: 'MS', name: 'Mississippi', rate: 0.05 },
  { code: 'MO', name: 'Missouri', rate: 0.054 },
  { code: 'MT', name: 'Montana', rate: 0.0675 },
  { code: 'NE', name: 'Nebraska', rate: 0.0684 },
  { code: 'NV', name: 'Nevada', rate: 0.00 },
  { code: 'NH', name: 'New Hampshire', rate: 0.05 },
  { code: 'NJ', name: 'New Jersey', rate: 0.1075 },
  { code: 'NM', name: 'New Mexico', rate: 0.049 },
  { code: 'NY', name: 'New York', rate: 0.109 },
  { code: 'NC', name: 'North Carolina', rate: 0.0475 },
  { code: 'ND', name: 'North Dakota', rate: 0.029 },
  { code: 'OH', name: 'Ohio', rate: 0.0399 },
  { code: 'OK', name: 'Oklahoma', rate: 0.05 },
  { code: 'OR', name: 'Oregon', rate: 0.099 },
  { code: 'PA', name: 'Pennsylvania', rate: 0.0307 },
  { code: 'RI', name: 'Rhode Island', rate: 0.0599 },
  { code: 'SC', name: 'South Carolina', rate: 0.07 },
  { code: 'SD', name: 'South Dakota', rate: 0.00 },
  { code: 'TN', name: 'Tennessee', rate: 0.00 },
  { code: 'TX', name: 'Texas', rate: 0.00 },
  { code: 'UT', name: 'Utah', rate: 0.0495 },
  { code: 'VT', name: 'Vermont', rate: 0.0875 },
  { code: 'VA', name: 'Virginia', rate: 0.0575 },
  { code: 'WA', name: 'Washington', rate: 0.00 },
  { code: 'WV', name: 'West Virginia', rate: 0.065 },
  { code: 'WI', name: 'Wisconsin', rate: 0.0765 },
  { code: 'WY', name: 'Wyoming', rate: 0.00 }
]

export function UserTaxProfile({ profile, onProfileUpdate, loading = false }: UserTaxProfileProps) {
  const { showSuccess, showError } = useToastHelpers()
  const [formState, setFormState] = useState<TaxProfileFormState>({
    filing_status: '',
    federal_tax_bracket: '',
    state: '',
    state_tax_rate: '',
    depreciation_method: '',
    material_participation: false,
    agi_estimate: '',
    errors: {},
    isSubmitting: false
  })

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Initialize form with existing profile data
  useEffect(() => {
    if (profile) {
      setFormState({
        filing_status: profile.filing_status,
        federal_tax_bracket: (profile.federal_tax_bracket * 100).toString(),
        state: profile.state,
        state_tax_rate: (profile.state_tax_rate * 100).toString(),
        depreciation_method: profile.depreciation_method,
        material_participation: profile.material_participation,
        agi_estimate: profile.agi_estimate.toString(),
        errors: {},
        isSubmitting: false
      })
      setHasUnsavedChanges(false)
    }
  }, [profile])

  const updateField = (field: keyof TaxProfileFormState, value: any) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
      errors: {
        ...prev.errors,
        [field]: '' // Clear error when user starts typing
      }
    }))
    setHasUnsavedChanges(true)
  }

  const updateStateSelection = (stateCode: string) => {
    const selectedState = US_STATES.find(s => s.code === stateCode)
    if (selectedState) {
      setFormState(prev => ({
        ...prev,
        state: stateCode,
        state_tax_rate: (selectedState.rate * 100).toString(),
        errors: {
          ...prev.errors,
          state: '',
          state_tax_rate: ''
        }
      }))
      setHasUnsavedChanges(true)
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formState.filing_status) {
      errors.filing_status = 'Filing status is required'
    }

    const federalRate = parseFloat(formState.federal_tax_bracket)
    if (!formState.federal_tax_bracket || isNaN(federalRate) || federalRate < 0 || federalRate > 50) {
      errors.federal_tax_bracket = 'Please enter a valid tax bracket (0-50%)'
    }

    if (!formState.state) {
      errors.state = 'State is required'
    }

    const stateRate = parseFloat(formState.state_tax_rate)
    if (!formState.state_tax_rate || isNaN(stateRate) || stateRate < 0 || stateRate > 20) {
      errors.state_tax_rate = 'Please enter a valid state tax rate (0-20%)'
    }

    if (!formState.depreciation_method) {
      errors.depreciation_method = 'Depreciation method is required'
    }

    const agi = parseFloat(formState.agi_estimate)
    if (!formState.agi_estimate || isNaN(agi) || agi < 0) {
      errors.agi_estimate = 'Please enter a valid AGI estimate'
    }

    setFormState(prev => ({ ...prev, errors }))
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      showError('Validation Error', 'Please correct the errors below')
      return
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }))

    try {
      const updatedProfile: Partial<UserTaxProfileType> = {
        filing_status: formState.filing_status as UserTaxProfileType['filing_status'],
        federal_tax_bracket: parseFloat(formState.federal_tax_bracket) / 100,
        state: formState.state,
        state_tax_rate: parseFloat(formState.state_tax_rate) / 100,
        depreciation_method: formState.depreciation_method as UserTaxProfileType['depreciation_method'],
        material_participation: formState.material_participation,
        agi_estimate: parseFloat(formState.agi_estimate),
        updated_at: new Date().toISOString()
      }

      // TODO: Make API call to update tax profile
      // const response = await apiClient.updateTaxProfile(updatedProfile)

      // For now, simulate the update
      if (onProfileUpdate && profile) {
        const fullProfile: UserTaxProfileType = {
          ...profile,
          ...updatedProfile
        }
        onProfileUpdate(fullProfile)
      }

      showSuccess('Tax Profile Updated', 'Your tax profile has been successfully updated')
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to update tax profile:', error)
      showError('Update Failed', 'Failed to update tax profile. Please try again.')
    } finally {
      setFormState(prev => ({ ...prev, isSubmitting: false }))
    }
  }

  const getSuggestedTaxBracket = (agi: number, filingStatus: string) => {
    const brackets = filingStatus === 'married_joint'
      ? TAX_BRACKETS_2024.married_joint
      : TAX_BRACKETS_2024.single

    for (const bracket of brackets) {
      if (agi >= bracket.min && agi < bracket.max) {
        return bracket.rate * 100
      }
    }
    return 37 // Highest bracket
  }

  const agiValue = parseFloat(formState.agi_estimate) || 0
  const suggestedBracket = formState.filing_status && agiValue > 0
    ? getSuggestedTaxBracket(agiValue, formState.filing_status)
    : null

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Tax Profile</h3>
            <p className="text-sm text-gray-600">
              Configure your tax information for accurate analysis
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Filing Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filing Status
            </label>
            <select
              value={formState.filing_status}
              onChange={(e) => updateField('filing_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select filing status</option>
              <option value="single">Single</option>
              <option value="married_joint">Married Filing Jointly</option>
              <option value="married_separate">Married Filing Separately</option>
              <option value="head_of_household">Head of Household</option>
            </select>
            {formState.errors.filing_status && (
              <p className="mt-1 text-sm text-red-600">{formState.errors.filing_status}</p>
            )}
          </div>

          {/* AGI Estimate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated AGI (Adjusted Gross Income)
            </label>
            <Input
              type="number"
              value={formState.agi_estimate}
              onChange={(e) => updateField('agi_estimate', e.target.value)}
              placeholder="e.g., 150000"
              error={formState.errors.agi_estimate}
            />
          </div>

          {/* Federal Tax Bracket */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Federal Tax Bracket (%)
              {suggestedBracket && (
                <span className="ml-2 text-sm text-blue-600">
                  (Suggested: {suggestedBracket}%)
                </span>
              )}
            </label>
            <Input
              type="number"
              value={formState.federal_tax_bracket}
              onChange={(e) => updateField('federal_tax_bracket', e.target.value)}
              placeholder="e.g., 22"
              min="0"
              max="50"
              step="0.1"
              error={formState.errors.federal_tax_bracket}
            />
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State of Residence
            </label>
            <select
              value={formState.state}
              onChange={(e) => updateStateSelection(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select state</option>
              {US_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.rate === 0 ? 'No State Tax' : `${(state.rate * 100).toFixed(1)}%`})
                </option>
              ))}
            </select>
            {formState.errors.state && (
              <p className="mt-1 text-sm text-red-600">{formState.errors.state}</p>
            )}
          </div>

          {/* State Tax Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State Tax Rate (%)
            </label>
            <Input
              type="number"
              value={formState.state_tax_rate}
              onChange={(e) => updateField('state_tax_rate', e.target.value)}
              placeholder="e.g., 6.5"
              min="0"
              max="20"
              step="0.1"
              error={formState.errors.state_tax_rate}
            />
          </div>

          {/* Depreciation Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Depreciation Method
            </label>
            <select
              value={formState.depreciation_method}
              onChange={(e) => updateField('depreciation_method', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select method</option>
              <option value="straight_line">Straight Line (Most Common)</option>
              <option value="accelerated">Accelerated (MACRS)</option>
              <option value="bonus">Bonus Depreciation (When Applicable)</option>
            </select>
            {formState.errors.depreciation_method && (
              <p className="mt-1 text-sm text-red-600">{formState.errors.depreciation_method}</p>
            )}
          </div>
        </div>

        {/* Material Participation */}
        <div className="mt-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                checked={formState.material_participation}
                onChange={(e) => updateField('material_participation', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3">
              <label className="text-sm font-medium text-gray-700">
                Material Participation in Real Estate Activities
              </label>
              <p className="text-sm text-gray-500">
                Check if you materially participate in real estate activities (typically for real estate professionals).
                This affects passive activity loss rules and deduction limits.
              </p>
            </div>
          </div>
        </div>

        {/* Tax Information Notice */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-amber-800">Tax Calculation Notice</h4>
              <p className="text-sm text-amber-700 mt-1">
                This information is used for estimation purposes only. Tax laws are complex and change frequently.
                Always consult with a qualified tax professional for specific tax advice and filing requirements.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200">
          <div className="flex items-center">
            {hasUnsavedChanges && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Unsaved changes
              </div>
            )}
            {profile && !hasUnsavedChanges && (
              <div className="flex items-center text-green-600 text-sm">
                <CheckCircle className="w-4 h-4 mr-1" />
                Profile saved
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={formState.isSubmitting || (!hasUnsavedChanges && !!profile)}
            className="flex items-center gap-2"
          >
            {formState.isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {profile ? 'Update Profile' : 'Save Profile'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}